import { useEffect, useState } from 'react';
// same layout + styles as the activity / guide "Select Travellers" popup
import '../../activityDetails/activityBookingTab/activityBookingTabFun/bookingTab/noPeopleSelect.css';

/*
 * Popup content: guests + how many rooms/tents of the selected unit.
 *
 * Rooms can't go below what the guests need (guests / people per unit)
 * or above the units the property has.
 *
 *   value          = { adults, children, pets, rooms }
 *   onApply        = called with { adults, children, pets, rooms, reserve } on "Continue"
 *   petsAllowed    = the stay takes pets (else pets stay at 0; up to MAX_PETS)
 *   reserveChecked = reserve (part payment) currently chosen
 *   reserveFor     = (guests, rooms) => reserve amount (0 = no reserve option)
 *
 * Several units selected (multi): only guests here — the rooms of each unit
 * are picked in "View all units"; guests can't go above what they fit.
 *   capacity       = guests the selected units fit
 *   selectionLabel = "Deluxe Tent × 1, Dorm Bed × 2"
 *   totalFor       = (guests, rooms) => stay price before tax
 */

// same limit as the server (backend utils/search/campAvailability.js)
export const MAX_PETS = 5;

const needed = (guests, perUnit) => Math.max(1, Math.ceil(guests / Math.max(perUnit, 1)));

export default function CampGuestSelect({
    value,
    onApply,
    unitName = 'Room',
    perUnit = 2,
    maxRooms = 1,
    price = 0,
    priceUnit = 'per_night',
    nights = 1,
    currencySymbol = '₹',
    formatPrice = (n) => n,
    reserveChecked = false,
    reserveFor = () => 0,
    multi = false,
    petsAllowed = false,
    capacity = 0,
    selectionLabel = '',
    totalFor = null
}) {
    const [draft, setDraft] = useState({ pets: 0, ...value, reserve: reserveChecked });
    const [warning, setWarning] = useState('');

    // take the latest values each time the popup opens / they change
    useEffect(
        () => setDraft({ pets: 0, ...value, reserve: reserveChecked }),
        [value.adults, value.children, value.pets, value.rooms, reserveChecked] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const guests = draft.adults + draft.children;
    const reserveTotal = reserveFor(guests, draft.rooms);
    const maxGuests = multi ? capacity : perUnit * maxRooms;
    const minRooms = needed(guests, perUnit);

    const change = (key, delta) => {
        setWarning('');
        setDraft((prev) => {
            const next = { ...prev, [key]: prev[key] + delta };
            if (next.adults < 1 || next.children < 0 || next.pets < 0 || next.rooms < 1) return prev;

            // pets don't take a guest place or a room
            if (key === 'pets') {
                if (!petsAllowed) {
                    setWarning("Pets aren't allowed at this stay");
                    return prev;
                }
                if (next.pets > MAX_PETS) {
                    setWarning(`Up to ${MAX_PETS} pets per booking`);
                    return prev;
                }
                return next;
            }

            if (key === 'rooms') {
                if (next.rooms > maxRooms) {
                    setWarning(`Only ${maxRooms} ${unitName} available`);
                    return prev;
                }
                if (next.rooms < needed(next.adults + next.children, perUnit)) {
                    setWarning(`${prev.adults + prev.children} guests need at least ${prev.rooms} ${unitName}`);
                    return prev;
                }
                return next;
            }

            // more guests -> add rooms when needed
            const total = next.adults + next.children;
            if (total > maxGuests) {
                setWarning(
                    multi
                        ? `The selected units fit ${maxGuests} guests — add a unit in "View all units"`
                        : `Maximum ${maxGuests} guests for this ${unitName}`
                );
                return prev;
            }
            if (multi) return next;
            next.rooms = Math.max(next.rooms, needed(total, perUnit));
            return next;
        });
    };

    const perPerson = priceUnit === 'per_person';
    const stayTotal = totalFor ? totalFor(guests, draft.rooms) : price * nights * (perPerson ? guests : draft.rooms);
    const unitLabel = multi ? selectionLabel : draft.rooms === 1 ? unitName : `${unitName} × ${draft.rooms}`;

    const rows = [
        { key: 'adults', title: 'Adults', sub: 'Age 13+' },
        { key: 'children', title: 'Children', sub: 'Age 2–12' },
        {
            key: 'pets',
            title: 'Pets',
            sub: petsAllowed ? `Allowed here · up to ${MAX_PETS}` : 'Not allowed at this stay',
            locked: !petsAllowed
        },
        ...(multi
            ? []
            : [
                  {
                      key: 'rooms',
                      title: unitName,
                      sub: `Up to ${perUnit} guests each · ${maxRooms} available`,
                      price: true
                  }
              ])
    ];

    return (
        <div className='ts-wrapper'>
            <div className='ts-content'>
                <div className='ts-header'>
                    <h2>Select Guests</h2>
                    <p>
                        {multi
                            ? `Choose the guests for ${selectionLabel} (fits ${capacity}).`
                            : `Choose the guests and how many ${unitName} you need.`}
                    </p>

                    {reserveTotal > 0 && (
                        <div className='reserve-card accommodationReserve'>
                            <input
                                type='checkbox'
                                id='campReserve'
                                checked={draft.reserve}
                                onChange={(e) => setDraft((d) => ({ ...d, reserve: e.target.checked }))}
                            />
                            <label htmlFor='campReserve' className='reserve-content'>
                                <div className='reserve-row'>
                                    <span className='reserve-title'>Reserve Now</span>
                                </div>
                                <div className='reserve-subtitle'>Pay the remaining amount later</div>
                            </label>
                        </div>
                    )}
                </div>

                <div className='ts-cards'>
                    {rows.map((row) => {
                        const qty = draft[row.key];
                        const minusDisabled =
                            row.key === 'adults' ? qty <= 1 : row.key === 'rooms' ? qty <= minRooms : qty <= 0;
                        // pets: + off when not allowed (− stays on to remove pets from an old search)
                        const plusDisabled = row.locked || (row.key === 'pets' && qty >= MAX_PETS);
                        return (
                            <div key={row.key} className={`ts-card ${qty > 0 ? 'ts-card-active' : ''}`}>
                                <div className='ts-card-left'>
                                    <div className='ts-card-title'>
                                        <h3>{row.title}</h3>
                                    </div>
                                    {row.price && (
                                        <div className='ts-price-row'>
                                            <span className='ts-price'>
                                                {currencySymbol}
                                                {formatPrice(price)}
                                            </span>
                                            <span className='ts-person-text'>
                                                {perPerson ? 'per person / night' : 'per night'}
                                            </span>
                                        </div>
                                    )}
                                    <div className='ts-terms'>
                                        <p>{row.sub}</p>
                                    </div>
                                </div>

                                <div className='ts-card-right'>
                                    <div className='ts-counter'>
                                        <button
                                            type='button'
                                            disabled={minusDisabled}
                                            onClick={() => change(row.key, -1)}
                                        >
                                            −
                                        </button>
                                        <span>{qty}</span>
                                        <button
                                            type='button'
                                            disabled={plusDisabled}
                                            onClick={() => change(row.key, 1)}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {warning && <p className='reserveText'>{warning}</p>}
                {!warning && !petsAllowed && draft.pets > 0 && (
                    <p className='reserveText'>Pets aren't allowed at this stay — please set pets to 0.</p>
                )}
            </div>

            <div className='ts-footer'>
                <div className='ts-footer-top'>
                    <div className='ts-summary'>
                        <span>
                            {guests} Guest{guests > 1 ? 's' : ''}
                            {draft.pets > 0 ? ` · ${draft.pets} Pet${draft.pets > 1 ? 's' : ''}` : ''} · {unitLabel}
                            {nights > 1 ? ` · ${nights} nights` : ''}
                        </span>
                        <h2>
                            {currencySymbol}
                            {formatPrice(stayTotal)}
                        </h2>
                        <small>
                            {reserveTotal > 0
                                ? `Reserve Now at ${currencySymbol}${formatPrice(reserveTotal)}`
                                : 'Excluding taxes'}
                        </small>
                    </div>
                </div>

                <button
                    type='button'
                    className='ts-continue'
                    onClick={() => onApply({ ...draft, reserve: draft.reserve && reserveTotal > 0 })}
                >
                    Continue ({guests})
                </button>
            </div>
        </div>
    );
}
